import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/client';
import { useQuery, useMutation } from 'urql';
import gql from 'graphql-tag';
import { useForm } from 'react-hook-form';
import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Button,
  SimpleGrid,
  Text,
  Heading,
  Select,
  Link
} from '@chakra-ui/core';

import DatePicker from 'react-datepicker';

const hasuraUserQuery = gql`
  query fetchUser($userId: uuid!) {
    users_by_pk(id: $userId) {
      id
      username
      email
      snooze_enabled
      snooze_until
    }
  }
`;


export default function Page() {
  const [session, loading] = useSession();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [snoozeEnabled, setSnoozeEnabled] = useState('');
  const [snoozeUntil, setSnoozeUntil] = useState(null);
  const [showUnsub, setShowUnsub] = useState(false);


  const [
    // eslint-disable-next-line no-unused-vars
    { data: fetchUserData, fetching: fetchUserFetching, error: fetchUserError },
  ] = useQuery({
    query: hasuraUserQuery,
    variables: {
      userId: session.id,
    },
  });

  useEffect(() => {
    if (fetchUserData) {
      const { username, email, snooze_enabled, snooze_until } = fetchUserData.users_by_pk;

      setName(username || '');
      setEmail(email || '');
      setSnoozeEnabled(snooze_enabled);
      if(snooze_until){
        // only set snooze until when its not null
        // the datepicker flips out if you try to create a date with null
        setSnoozeUntil(new Date(snooze_until));
      }
    }
  }, [fetchUserData]);

  const { handleSubmit, errors, register, formState } = useForm();

  const hasuraUserMutation = gql`
    mutation updateUser(
      $userId: uuid!
      $username: String
      $snooze_enabled: Boolean
      $snooze_until: timestamptz
    ) {
      update_users_by_pk(
        pk_columns: { id: $userId }
        _set: {
          username: $username
          snooze_enabled: $snooze_enabled
          snooze_until: $snooze_until
        }
      ) {
        email
        username
        snooze_enabled
        snooze_until
      }
    }
  `;

  const [, updateUser] = useMutation(hasuraUserMutation);
  const [userMutationError, setUserMutationError] = useState('');

  async function onSubmit(values) {
    setUserMutationError(null);

    const res = await updateUser({
      userId: session.id,
      username: values.name,
      snooze_enabled: values.snooze_enabled,
      snooze_until: snoozeUntil || null
    });

    if(res.error){
      setUserMutationError(res.error.message);
      return false;
    }

    const { username, email, snooze_enabled, snooze_until } = res.data.update_users_by_pk;

    setName(username || '');
    setEmail(email || '');
    setSnoozeEnabled(snooze_enabled);
    if(snooze_until){
      // only set snooze until when its not null
      // the datepicker flips out if you try to create a date with null
      setSnoozeUntil(new Date(snooze_until));
    }
  }

  // When rendering client side don't display anything until loading is complete
  if (typeof window !== 'undefined' && loading) return null;

  const handleNameChange = (event) => setName(event.target.value);
  const handleSnoozeChange = () => setSnoozeEnabled(!snoozeEnabled);
  const handleUnSubChange = () => setShowUnsub(!showUnsub);

  return (
    <SimpleGrid columns={[1]} spacing="24px">

      <Heading as="h1" size="lg" mb={2}>Profile Page</Heading>

      {fetchUserError ? <Text fontSize="lg" color="tomato">{fetchUserError}</Text> : null}
      {userMutationError ? <Text fontSize="lg" color="tomato">{userMutationError}</Text> : null}

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl isReadOnly >
          <FormLabel htmlFor="email">Email (to change email, pls signup again with new email for now ... )</FormLabel>
          <Input
            value={email}
            name="email"
            isDisabled
          />
          <FormErrorMessage>
            {errors.email && errors.email.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl mt={4} isInvalid={errors.name}>
          <FormLabel htmlFor="name">HN Username </FormLabel>
          <Input
            value={name}
            name="name"
            placeholder="pg"
            onChange={handleNameChange}
            ref={register({ required: true })}
          />
          <FormErrorMessage>
            {errors.name && 'Username is required'}
          </FormErrorMessage>
        </FormControl>
        
        <FormControl mt={4} isInvalid={errors.snooze_enabled}>
          <FormLabel htmlFor="snooze_enabled"> Snooze? </FormLabel>
          <Select
            value={snoozeEnabled}
            name="snooze_enabled"
            onChange={handleSnoozeChange}
            ref={register}
          >
            <option value="true"> True </option>
            <option value="false"> False </option>
          </Select>
        </FormControl>

        {!!snoozeEnabled &&
          <FormControl mt={4} isInvalid={errors.snooze_until}>
            <FormLabel htmlFor="snooze_until"> 
              Snooze Until a Day in the future (leave blank for indefinite) 
            </FormLabel>
            <DatePicker selected={snoozeUntil} onChange={date => setSnoozeUntil(date)} />
          </FormControl>
        }

        <FormControl mt={4}>
          <FormLabel htmlFor="show_unsub"> Show unsubscribe page link (appears at bottom of this page) </FormLabel>
          <Select
            value={showUnsub}
            name="show_unsub"
            onChange={handleUnSubChange}
            ref={register}
          >
            <option value="true"> True </option>
            <option value="false"> False </option>
          </Select>
        </FormControl>

        <Button
          mt={4}
          mb={4}
          colorScheme="teal"
          isLoading={formState.isSubmitting}
          type="submit"
        >
        Submit
        </Button>
      </form>

      {showUnsub && <Link href="/unsubscribe"><a> Unsubscribe aka Delete Profile </a></Link>}
    </SimpleGrid>
  );
}
